import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Compass, RefreshCw, ArrowRight, ArrowUpRight, Gift, X, Loader2, FolderPlus,
  Lightbulb, PenTool, TrendingUp, Package,
  Target, Megaphone, Sparkles, BookOpen, Layers, ImageIcon, Wand2, CalendarDays,
} from "lucide-react";
import { toast } from "sonner";

const MOVE_ICONS = {
  target: Target, megaphone: Megaphone, sparkles: Sparkles, book: BookOpen,
  layers: Layers, trending: TrendingUp, image: ImageIcon, wand: Wand2,
  calendar: CalendarDays, folder: FolderPlus,
};

const GREETINGS = ["Welcome back", "Good to see you", "Let's make moves", "Ready when you are"];

// Goal → single recommended next action shown when arriving from the Guided transition.
const GUIDED_NEXT = {
  get_leads: { label: "Create lead-gen ads", why: "Turn your hook into ads designed to capture emails.", route: "/ad-creator", cta: "Create ads" },
  drive_traffic: { label: "Create traffic ads", why: "Turn your hook into ads that drive clicks to your content.", route: "/ad-creator", cta: "Create ads" },
  make_sales: { label: "Build your sales campaign", why: "Turn your offer into a full content campaign built to sell.", route: "/campaigns/new", cta: "Build campaign" },
  sell_product: { label: "Build your sales campaign", why: "Turn your offer into a full content campaign built to sell.", route: "/campaigns/new", cta: "Build campaign" },
  grow_audience: { label: "Build a content campaign", why: "Turn your plan into a week of scroll-stopping content.", route: "/campaigns/new", cta: "Build campaign" },
  promote_affiliate: { label: "Build a promo campaign", why: "Turn your hook into content that promotes your product.", route: "/campaigns/new", cta: "Build campaign" },
  first_100: { label: "Build your first campaign", why: "Turn your plan into content that lands your first sales.", route: "/campaigns/new", cta: "Build campaign" },
  other: { label: "Build a content campaign", why: "Turn your plan into a full content campaign.", route: "/campaigns/new", cta: "Build campaign" },
};

function SummaryChip({ icon: Icon, label, value }) {
  return (
    <div className="flex max-w-xs items-start gap-2 rounded-xl border border-border bg-card px-3 py-2">
      <Icon size={15} className="mt-0.5 shrink-0 text-primary" />
      <span className="min-w-0">
        <span className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
        <span className="block truncate text-sm font-medium">{value}</span>
      </span>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { credits } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showWelcome, setShowWelcome] = useState(
    () => localStorage.getItem("h2i_seen_welcome_credits") !== "1"
  );
  const [fromGuided, setFromGuided] = useState(() => {
    try { return sessionStorage.getItem("h2i_from_guided") === "1"; } catch { return false; }
  });
  const [guided, setGuided] = useState(null);

  useEffect(() => {
    api.get("/coach/home")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!fromGuided) return;
    api.get("/journey")
      .then((r) => {
        const j = r.data?.journey;
        if (j?.completed && j?.data) setGuided(j);
        else setFromGuided(false);
      })
      .catch((e) => { console.warn("Could not load guided summary:", e); setFromGuided(false); });
  }, [fromGuided]);

  const dismissGuided = () => {
    try { sessionStorage.removeItem("h2i_from_guided"); } catch (e) { console.warn(e); }
    setFromGuided(false);
  };

  const refreshReco = async () => {
    setRefreshing(true);
    try {
      const { data: r } = await api.post("/coach/refresh");
      setData((d) => ({ ...d, recommendation: r.recommendation }));
      toast.success("Fresh guidance from your coach");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't refresh right now. Try again.");
    } finally {
      setRefreshing(false);
    }
  };

  // "Build a Product" routes into the Offer Profile / Pitch Test flow.
  const buildProductRoute = () => {
    const ap = data?.active_project;
    if (!ap?.offer_id) return "/projects/new";
    return ap.has_offer && !ap.pitch_done ? `/pitch-test/${ap.offer_id}` : `/offer-profiles/${ap.offer_id}`;
  };

  const quickActions = [
    { key: "hook", label: "Find a Hook", desc: "Scroll-stopping openers", icon: Lightbulb,
      color: "text-primary", route: () => "/hooks", testid: "quick-find-hook" },
    { key: "content", label: "Create Content", desc: "Build a campaign", icon: PenTool,
      color: "text-accent", route: () => "/campaigns/new", testid: "quick-create-content" },
    { key: "income", label: "Find an Income Opportunity", desc: "A quick earning play", icon: TrendingUp,
      color: "text-primary", route: () => "/income-mode", testid: "quick-income-opportunity" },
    { key: "product", label: "Build a Product", desc: "Shape & test your offer", icon: Package,
      color: "text-accent", route: buildProductRoute, testid: "quick-build-product" },
  ];

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center" data-testid="dashboard-loading">
        <Loader2 size={30} className="animate-spin text-primary" />
      </div>
    );
  }

  const reco = data?.recommendation;
  const moves = data?.next_moves || [];
  const ap = data?.active_project;
  const greeting = GREETINGS[new Date().getDay() % GREETINGS.length];

  return (
    <div className="space-y-10" data-testid="dashboard">
      {showWelcome && credits && !credits.unlimited && (
        <div data-testid="welcome-credits-banner"
          className="fade-up flex items-center justify-between gap-3 rounded-2xl border border-accent/30 bg-accent/10 px-5 py-3.5">
          <div className="flex items-center gap-3">
            <Gift size={18} className="shrink-0 text-accent" />
            <p className="text-sm">Welcome! You've got <span className="font-semibold">{credits.balance} free credits</span> to start.{" "}
              <button onClick={() => navigate("/pricing")} className="font-semibold text-primary underline">See plans</button>.</p>
          </div>
          <button onClick={() => { localStorage.setItem("h2i_seen_welcome_credits", "1"); setShowWelcome(false); }}
            data-testid="welcome-dismiss" className="rounded-lg p-1.5 text-muted-foreground hover:bg-secondary">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Contextual continuation from Guided Mode (replaces generic greeting) */}
      {fromGuided && guided ? (() => {
        const gd = guided.data || {};
        const gHook = (gd.hook || {}).hook || "";
        const gOffer = gd.offer || {};
        const next = GUIDED_NEXT[gd.earn_goal] || GUIDED_NEXT.other;
        const contMsg = gOffer.name
          ? `Your offer "${gOffer.name}" is ready. Here's what to build next.`
          : gHook ? "You've got your hook. Here's what to build next."
          : "Your plan's ready. Here's what to build next.";
        return (
          <div className="fade-up" data-testid="guided-continuation">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Continuing from Guided Mode</p>
                <h1 className="mt-1 font-head text-4xl font-bold tracking-tight sm:text-5xl" data-testid="continuation-message">
                  {contMsg}
                </h1>
                <p className="mt-1 text-muted-foreground">Your Guided work is saved — here's the one move I'd make next.</p>
              </div>
              <button onClick={dismissGuided} data-testid="continuation-dismiss" title="Explore freely"
                className="shrink-0 rounded-lg p-1.5 text-muted-foreground hover:bg-secondary">
                <X size={18} />
              </button>
            </div>

            <div className="mt-5 flex flex-wrap gap-2" data-testid="guided-summary">
              {gHook && <SummaryChip icon={Lightbulb} label="Your hook" value={gHook} />}
              {gOffer.name && <SummaryChip icon={Package} label="Your offer" value={gOffer.name} />}
              {gOffer.pricing && <SummaryChip icon={TrendingUp} label="Suggested pricing for your course offer" value={gOffer.pricing} />}
              {gd.content_plan && <SummaryChip icon={CalendarDays} label="Content plan" value="7-day plan ready" />}
              {gd.ads && <SummaryChip icon={Megaphone} label="Ads" value="3 platform ads" />}
            </div>
            {gOffer.pricing && (
              <p className="mt-2 text-xs text-muted-foreground" data-testid="guided-pricing-note">
                This is what you could charge your customers for this offer — not what you'll be charged.
              </p>
            )}

            <Card className="mt-6 flex flex-col gap-4 border-l-4 border-l-primary bg-primary/5 p-8 sm:flex-row sm:items-center sm:justify-between"
              data-testid="guided-next-action">
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
                  <Compass size={13} /> Recommended next
                </span>
                <h2 className="mt-3 font-head text-2xl font-bold tracking-tight">{next.label}</h2>
                <p className="mt-1 max-w-xl text-muted-foreground">{next.why}</p>
              </div>
              <Button onClick={() => { dismissGuided(); navigate(next.route); }} data-testid="guided-next-action-btn"
                className="shrink-0 rounded-full bg-primary px-7 py-6 text-base font-semibold hover:bg-primary/90">
                {next.cta} <ArrowRight size={18} className="ml-1.5" />
              </Button>
            </Card>
          </div>
        );
      })() : (
        <div className="fade-up flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Your coach</p>
            <h1 className="font-head text-4xl font-bold tracking-tight sm:text-5xl" data-testid="coach-greeting">
              {greeting}{data?.user_name ? `, ${data.user_name}` : ""}.
            </h1>
            <p className="mt-1 text-muted-foreground">
              {ap ? <>Here's your smartest next move for <span className="font-medium text-foreground">{ap.name}</span>.</>
                  : "Let's set up your first project so I can start coaching you."}
            </p>
          </div>
          {ap && (
            <Button variant="outline" onClick={() => navigate("/projects")} data-testid="view-projects-btn"
              className="w-fit rounded-full">
              <Compass size={16} className="mr-1.5" /> All projects
            </Button>
          )}
        </div>
      )}

      {/* No active project */}
      {!ap ? (
        <Card className="fade-up flex flex-col items-center gap-4 border-dashed border-border p-12 text-center"
          data-testid="dashboard-no-project" style={{ animationDelay: "0.1s" }}>
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <FolderPlus size={26} />
          </div>
          <div>
            <p className="font-head text-lg font-semibold">Start your first project</p>
            <p className="text-sm text-muted-foreground">Pick a goal and set up your offer — I'll take it from there.</p>
          </div>
          <Button onClick={() => navigate("/projects/new")} data-testid="create-first-project-btn"
            className="rounded-full bg-primary px-6 hover:bg-primary/90">
            <FolderPlus size={18} className="mr-1.5" /> Create project
          </Button>
        </Card>
      ) : (
        <>
          {/* Hero recommendation + next best moves */}
          <div className="grid gap-6 md:grid-cols-12 md:gap-8">
            {/* Dominant coach recommendation */}
            <Card
              className="fade-up relative flex min-h-[280px] flex-col justify-between overflow-hidden border-l-4 border-l-primary bg-primary/5 p-8 md:col-span-8"
              data-testid="coach-recommendation-card" style={{ animationDelay: "0.05s" }}>
              <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-primary/10 blur-2xl" />
              <div className="relative">
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
                    <Compass size={13} /> Opportunity spotlight
                  </span>
                  <button onClick={refreshReco} disabled={refreshing} data-testid="refresh-recommendation-btn"
                    title="Get fresh guidance"
                    className="flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary disabled:opacity-60">
                    <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> Refresh
                  </button>
                </div>
                {reco ? (
                  <>
                    <h2 className="mt-5 font-head text-3xl font-bold tracking-tight" data-testid="coach-reco-headline">
                      {reco.headline}
                    </h2>
                    <p className="mt-3 max-w-2xl text-base leading-relaxed text-foreground/80" data-testid="coach-reco-message">
                      {reco.message}
                    </p>
                  </>
                ) : (
                  <p className="mt-6 text-muted-foreground" data-testid="coach-reco-empty">
                    Set up your offer and I'll spotlight your best opportunity here.
                  </p>
                )}
              </div>
              {reco && (
                <div className="relative mt-8">
                  <Button onClick={() => navigate(reco.action_route)} data-testid="coach-reco-action-btn"
                    className="rounded-full bg-primary px-6 py-6 text-base font-semibold transition-transform duration-100 hover:bg-primary/90 active:scale-95">
                    {reco.action_label} <ArrowRight size={18} className="ml-1.5" />
                  </Button>
                </div>
              )}
            </Card>

            {/* Next best moves */}
            <div className="fade-up flex flex-col gap-4 md:col-span-4" style={{ animationDelay: "0.15s" }}
              data-testid="next-best-moves">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Next best moves</p>
              {moves.map((m, i) => {
                const Icon = MOVE_ICONS[m.icon] || Sparkles;
                return (
                  <button key={m.route || i} onClick={() => navigate(m.route)} data-testid={`next-move-${i}`}
                    className="group flex items-start gap-3 rounded-xl border border-border bg-card p-4 text-left transition-transform duration-200 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md">
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary transition-colors group-hover:bg-primary/10">
                      <Icon size={17} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="font-semibold leading-tight">{m.title}</span>
                        <ArrowUpRight size={15} className="shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
                      </span>
                      <span className="mt-0.5 block text-sm text-muted-foreground">{m.desc}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quick actions */}
          <div className="fade-up" style={{ animationDelay: "0.25s" }}>
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Jump straight in</p>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {quickActions.map((a) => {
                const Icon = a.icon;
                return (
                  <button key={a.key} onClick={() => navigate(a.route())} data-testid={a.testid}
                    className="group flex flex-col items-start gap-4 rounded-2xl border border-border bg-card p-6 text-left transition-transform duration-200 hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg">
                    <span className={`flex h-11 w-11 items-center justify-center rounded-xl bg-secondary ${a.color} transition-transform duration-200 group-hover:scale-105`}>
                      <Icon size={22} />
                    </span>
                    <span>
                      <span className="block font-head font-semibold leading-tight">{a.label}</span>
                      <span className="mt-1 block text-sm text-muted-foreground">{a.desc}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
